/**
 * Analytics Dashboard Component
 *
 * Comprehensive analytics with recharts charts, shadcn Table, and full dark mode.
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  MessageCircle,
  TrendingUp,
  RefreshCw,
  Target,
  Clock,
  Monitor,
  Smartphone,
  Zap,
  Users,
  BarChart3,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Label,
} from 'recharts'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card'
import { Button } from '../../ui/Button'
import { Select } from '../../ui/Select'
import { Badge } from '../../ui/Badge'
import { Progress } from '../../ui/progress'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../ui/table'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../ui/chart'
import type { ChartConfig } from '../../ui/chart'

import { analyticsService } from '../../../services/analytics'
import { api } from '../../../services/api'
import { logger } from '../../../utils/logger'
import { cn } from '../../../utils/cn'
import { formatDuration } from '../../../utils/time'

import type {
  AnalyticsDashboard as AnalyticsDashboardData,
  AIPerformanceAnalytics,
  MessageRatingsAnalytics,
  CustomerJourneyAnalytics,
  ChatbotPerformance,
} from '../../../services/analytics'

// ====================================
// CHART CONFIGS
// ====================================

const trendsChartConfig = {
  conversations: { label: 'Conversations', color: 'hsl(var(--chart-1))' },
  messages: { label: 'Messages', color: 'hsl(var(--chart-2))' },
} satisfies ChartConfig

const ratingsChartConfig = {
  positive: { label: 'Positive', color: 'hsl(142 76% 36%)' },
  negative: { label: 'Negative', color: 'hsl(0 84% 60%)' },
} satisfies ChartConfig

// ====================================
// TYPES
// ====================================

interface AnalyticsDashboardProps {
  className?: string
}

// Extended types for fields present in API response but absent from TS definitions
type OverviewData = AnalyticsDashboardData['overview'] & { ai_credits?: number }
type ChatbotPerfExt = ChatbotPerformance & { ai_credits?: number }

// ====================================
// STAT CARD
// ====================================

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
}) {
  const display = typeof value === 'number' ? value.toLocaleString() : value
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-3xl font-semibold text-foreground mt-1">{display}</p>
          </div>
          <div className="rounded-full bg-muted p-3">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ====================================
// MAIN COMPONENT
// ====================================

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  className,
}) => {
  // ── State ──
  const [analytics, setAnalytics] = useState<AnalyticsDashboardData | null>(null)
  const [aiPerformance, setAiPerformance] = useState<AIPerformanceAnalytics | null>(null)
  const [messageRatings, setMessageRatings] = useState<MessageRatingsAnalytics | null>(null)
  const [customerJourney, setCustomerJourney] = useState<CustomerJourneyAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState('30')
  const [selectedChatbot, setSelectedChatbot] = useState('all')
  const [chatbots, setChatbots] = useState<Array<{ id: string; name: string }>>([])
  const [isRefreshing, setIsRefreshing] = useState(false)

  // ── Data Fetching ──

  const fetchChatbots = async () => {
    try {
      const response = await api.get('/chatbots')
      const chatbotsData = response.data.chatbots || []
      setChatbots(chatbotsData.map((bot: { id: string; name: string }) => ({ id: bot.id, name: bot.name })))
    } catch (error) {
      logger.error('Failed to fetch chatbots', { error })
    }
  }

  const fetchAnalytics = async (days: number = 30, chatbotId?: string) => {
    try {
      setError(null)

      const [dashboardData, aiData, ratingsData, journeyData] = await Promise.all([
        analyticsService.getAnalyticsDashboard(days, chatbotId === 'all' ? undefined : chatbotId),
        analyticsService.getAIPerformanceAnalytics(days, chatbotId === 'all' ? undefined : chatbotId),
        analyticsService.getMessageRatingsAnalytics(days, chatbotId === 'all' ? undefined : chatbotId),
        analyticsService.getCustomerJourneyAnalytics(days, chatbotId === 'all' ? undefined : chatbotId),
      ])

      setAnalytics(dashboardData)
      setAiPerformance(aiData)
      setMessageRatings(ratingsData)
      setCustomerJourney(journeyData)

      logger.info('Analytics dashboard loaded successfully', {
        component: 'AnalyticsDashboard',
        action: 'analytics_loaded',
        period: `${dashboardData.period_start} to ${dashboardData.period_end}`,
        aiResponses: aiData.total_ai_responses,
        ratings: ratingsData.total_ratings,
        sessions: journeyData.total_sessions,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load analytics'
      setError(errorMessage)

      logger.error('Failed to fetch analytics dashboard', {
        component: 'AnalyticsDashboard',
        action: 'fetch_error',
        error: errorMessage,
      })
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  // ── Effects ──

  useEffect(() => {
    fetchChatbots()
    fetchAnalytics(parseInt(selectedPeriod), selectedChatbot)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod])

  // ── Handlers ──

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await fetchAnalytics(parseInt(selectedPeriod), selectedChatbot)
  }

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period)
    setIsLoading(true)
  }

  const handleChatbotChange = (chatbotId: string) => {
    setSelectedChatbot(chatbotId)
    setIsLoading(true)
    fetchAnalytics(parseInt(selectedPeriod), chatbotId)
  }

  // ── Computed ──

  const overview = analytics?.overview as OverviewData | undefined
  const chatbotPerfs = (analytics?.chatbot_performance || []) as ChatbotPerfExt[]
  const satisfactionPct = messageRatings?.satisfaction_rate?.toFixed(0) ?? '0'

  const ratingsData = useMemo(
    () => [
      { name: 'positive', value: messageRatings?.positive_ratings || 0, fill: 'var(--color-positive)' },
      { name: 'negative', value: messageRatings?.negative_ratings || 0, fill: 'var(--color-negative)' },
    ],
    [messageRatings]
  )
  const hasRatings = ratingsData.some((d) => d.value > 0)

  // ── Render: Loading ──

  if (isLoading) {
    return (
      <div className={cn('space-y-6', className)}>
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading analytics...</span>
        </div>
      </div>
    )
  }

  // ── Render: Error ──

  if (error) {
    return (
      <div className={cn('space-y-6', className)}>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <BarChart3 className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button onClick={handleRefresh} variant="outline" className="mt-4">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!analytics) return null

  // ── Render: Main ──

  return (
    <div className={cn('space-y-6', className)}>
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
          <p className="text-sm text-muted-foreground">
            {new Date(analytics.period_start).toLocaleDateString()} &mdash;{' '}
            {new Date(analytics.period_end).toLocaleDateString()}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-full sm:w-[170px]">
            <Select
              value={selectedChatbot}
              onChange={handleChatbotChange}
              options={[
                { value: 'all', label: 'All Chatbots' },
                ...chatbots.map((bot) => ({ value: bot.id, label: bot.name })),
              ]}
            />
          </div>
          <div className="w-full sm:w-[130px]">
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              options={[
                { value: '1', label: 'Today' },
                { value: '2', label: 'Yesterday' },
                { value: '7', label: '7 days' },
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
              ]}
            />
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="icon"
            disabled={isRefreshing}
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* ── Row 1: Stat Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Conversations"
          value={overview?.total_conversations ?? 0}
          icon={MessageCircle}
        />
        <StatCard
          label="Messages"
          value={overview?.total_messages ?? 0}
          icon={TrendingUp}
        />
        <StatCard
          label="AI Usage"
          value={overview?.ai_credits ?? 0}
          icon={Zap}
        />
        <StatCard
          label="Satisfaction"
          value={`${satisfactionPct}%`}
          icon={Target}
        />
      </div>

      {/* ── Row 2: Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Area Chart — Conversation Trends */}
        <Card className="lg:col-span-8">
          <CardHeader>
            <CardTitle>Conversation Trends</CardTitle>
            <CardDescription>Last {analytics.recent_trends.length} days</CardDescription>
          </CardHeader>
          <CardContent>
            {!analytics.recent_trends.length ? (
              <div className="flex h-[280px] items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="mx-auto size-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No trend data available</p>
                </div>
              </div>
            ) : (
              <ChartContainer config={trendsChartConfig} className="h-[280px] w-full">
                <AreaChart
                  data={analytics.recent_trends}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="fillConvAnalytics" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-conversations)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="var(--color-conversations)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="fillMsgAnalytics" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-messages)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="var(--color-messages)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val: string) =>
                      new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    width={32}
                    tick={{ fontSize: 11 }}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        labelFormatter={(val: string) =>
                          new Date(val).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        }
                      />
                    }
                  />
                  <Area
                    dataKey="conversations"
                    type="monotone"
                    fill="url(#fillConvAnalytics)"
                    stroke="var(--color-conversations)"
                    strokeWidth={2}
                  />
                  <Area
                    dataKey="messages"
                    type="monotone"
                    fill="url(#fillMsgAnalytics)"
                    stroke="var(--color-messages)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Pie Chart — Message Ratings Donut */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle>Message Ratings</CardTitle>
            <CardDescription>Customer satisfaction breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            {!hasRatings ? (
              <div className="flex h-[280px] items-center justify-center">
                <div className="text-center">
                  <Target className="mx-auto size-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No ratings yet</p>
                </div>
              </div>
            ) : (
              <>
                <ChartContainer config={ratingsChartConfig} className="mx-auto aspect-square h-[220px]">
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                    <Pie
                      data={ratingsData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={85}
                      strokeWidth={0}
                    >
                      {ratingsData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                      <Label
                        content={({ viewBox }) => {
                          if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                            return (
                              <text
                                x={viewBox.cx}
                                y={viewBox.cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                              >
                                <tspan
                                  x={viewBox.cx}
                                  y={viewBox.cy}
                                  className="fill-foreground text-3xl font-bold"
                                >
                                  {satisfactionPct}%
                                </tspan>
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) + 24}
                                  className="fill-muted-foreground text-sm"
                                >
                                  Satisfaction
                                </tspan>
                              </text>
                            )
                          }
                        }}
                      />
                    </Pie>
                  </PieChart>
                </ChartContainer>
                <div className="flex justify-center gap-6 mt-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: 'hsl(142 76% 36%)' }}
                    />
                    <span className="text-sm text-muted-foreground">
                      Positive ({messageRatings?.positive_ratings || 0})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: 'hsl(0 84% 60%)' }}
                    />
                    <span className="text-sm text-muted-foreground">
                      Negative ({messageRatings?.negative_ratings || 0})
                    </span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Row 3: Detail Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* AI Performance */}
        {aiPerformance && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                AI Performance
              </CardTitle>
              <CardDescription>Response quality &amp; speed</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-semibold text-foreground">
                    {aiPerformance.avg_confidence_score?.toFixed(3) || '0.000'}
                  </div>
                  <div className="text-sm text-muted-foreground">Confidence</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-foreground">
                    {formatDuration(
                      ((aiPerformance.avg_processing_time_ms || 0) as number) / 1000
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">Response Time</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-foreground">
                    {aiPerformance.low_confidence_rate?.toFixed(1) || '0.0'}%
                  </div>
                  <div className="text-sm text-muted-foreground">Low Confidence</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-foreground">
                    {(aiPerformance.total_ai_responses || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground">AI Responses</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Journey */}
        {customerJourney && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Customer Journey
              </CardTitle>
              <CardDescription>Traffic &amp; devices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-2xl font-semibold text-foreground">
                      {(customerJourney.total_sessions || 0).toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Sessions</div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-foreground">
                      {formatDuration((customerJourney.avg_session_duration_minutes || 0) * 60)}
                    </div>
                    <div className="text-sm text-muted-foreground">Avg Duration</div>
                  </div>
                </div>

                {customerJourney.traffic_sources?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                      Top Sources
                    </p>
                    {customerJourney.traffic_sources.slice(0, 3).map((source) => (
                      <div key={source.source} className="flex justify-between items-center py-1">
                        <span className="text-sm text-foreground">{source.source}</span>
                        <span className="text-sm text-muted-foreground">
                          {source.sessions} ({source.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {customerJourney.device_breakdown?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                      Devices
                    </p>
                    {customerJourney.device_breakdown.map((device) => (
                      <div key={device.device} className="flex justify-between items-center py-1">
                        <span className="text-sm text-foreground flex items-center gap-1.5">
                          {device.device === 'desktop' ? (
                            <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          <span className="capitalize">{device.device}</span>
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {device.sessions} ({device.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Handoff Analytics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Handoff Analytics
            </CardTitle>
            <CardDescription>Human handoff metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-semibold text-foreground">
                    {analytics.handoff_analytics.total_handoffs}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Handoffs</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-foreground">
                    {analytics.handoff_analytics.pending_handoffs}
                  </div>
                  <div className="text-sm text-muted-foreground">Pending</div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-muted-foreground">Success Rate</span>
                  <span className="text-sm font-medium text-foreground">
                    {analytics.handoff_analytics.success_rate.toFixed(0)}%
                  </span>
                </div>
                <Progress value={analytics.handoff_analytics.success_rate} className="h-2" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-lg font-semibold text-foreground">
                      {formatDuration(
                        (analytics.handoff_analytics.avg_wait_time_minutes || 0) * 60
                      )}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">Wait Time</div>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-lg font-semibold text-foreground">
                      {formatDuration(
                        (analytics.handoff_analytics.avg_resolution_time_minutes || 0) * 60
                      )}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">Resolution</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Row 4: Chatbot Performance Table ── */}
      <Card>
        <CardHeader>
          <CardTitle>Chatbot Performance</CardTitle>
          <CardDescription>Individual chatbot metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Chatbot</TableHead>
                <TableHead className="text-right">Conversations</TableHead>
                <TableHead className="text-right">Messages</TableHead>
                <TableHead className="text-right">Success Rate</TableHead>
                <TableHead className="text-right">Avg Session</TableHead>
                <TableHead className="text-right">Last Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chatbotPerfs
                .sort((a, b) => b.total_conversations - a.total_conversations)
                .map((bot) => (
                  <TableRow key={bot.chatbot_id}>
                    <TableCell className="font-medium">{bot.chatbot_name}</TableCell>
                    <TableCell className="text-right">
                      {bot.total_conversations.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {bot.total_messages.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant={
                          bot.success_rate >= 80
                            ? 'success'
                            : bot.success_rate >= 50
                              ? 'warning'
                              : 'error'
                        }
                      >
                        {bot.success_rate.toFixed(0)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatDuration((bot.avg_session_length || 0) * 60)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {bot.last_active
                        ? new Date(bot.last_active).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                  </TableRow>
                ))}
              {!chatbotPerfs.length && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No chatbot data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export default AnalyticsDashboard
